import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilesOnly } from '../../fetchHooks/useFilesOnly';
import 'video-react/dist/video-react.css';
import './Myday.scss';
import type { FileType } from '../../fetchHooks/useAllFiles';
import type { MydayType } from '../../fetchHooks/useMyday';
import FileItem from './FileItem';
import styles from './Files.module.css';

export const useEscapeKey = (callback: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback]); // Empty dependency array ensures the effect is only run once
};

interface FilesProps {
  myday: MydayType;
  compact?: boolean;
  isEdit?: boolean;
}
export const Files = ({ myday, compact = false, isEdit = false }: FilesProps) => {
  const { t } = useTranslation();
  const regexVideo = /\.(mp4|avi|flv|wmv|mov|mkv|m4v)$/i;

  const { data, refetch, isLoading } = useFilesOnly(myday.id);
  const videos = data?.filter((file: FileType) => regexVideo.test(file.name));
  const otherFiles = data?.filter((file: FileType) => !regexVideo.test(file.name));
  if ((!videos?.length && !otherFiles?.length) || isLoading) return;
  if (isEdit)
    return (
      <>
        {videos.length > 0 && (
          <>
            <h3>{t('myday.videos')}:</h3>
            <div className={styles.container}>
              {videos?.map((file: FileType) => (
                <FileItem file={file} refetch={refetch} compact={compact} key={file.id} isEdit={isEdit} />
              ))}
            </div>
          </>
        )}
        {otherFiles.length > 0 && (
          <>
            <h3>{t('myday.otherFiles')}:</h3>
            <div className={styles.container}>
              {otherFiles?.map((file: FileType) => (
                <FileItem file={file} refetch={refetch} compact={compact} key={file.id} isEdit={isEdit} />
              ))}
            </div>
          </>
        )}
      </>
    );

  return (
    <>
      {videos.length > 0 && (
        <>
          <h3>{t('myday.videos')}:</h3>
          <div className={styles.filesGrid}>
            {videos?.map((file: FileType) => (
              <FileItem file={file} refetch={refetch} compact={compact} key={file.id} />
            ))}
          </div>
        </>
      )}
      {otherFiles.length > 0 && (
        <>
          <h3>{t('myday.otherFiles')}:</h3>
          <div className={styles.filesGrid}>
            {otherFiles?.map((file: FileType) => (
              <FileItem file={file} refetch={refetch} compact={compact} key={file.id} />
            ))}
          </div>
        </>
      )}
    </>
  );
};
